import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { Card } from 'src/components/UI/Card';
import { GenerateBlogPostDialog } from './GenerateBlogPostDialog';
import useBlogGenerationMutation from '../hooks/use-blog-generation-mutation';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

function BGNoDataFound({ siteSoftDeleted }: { siteSoftDeleted?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { _id: siteId, user_id: userId } = useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails) ?? {};

  const {
    dialogOpen,
    setDialogOpen,
    topics,
    setTopics,
    handleEnableBlogGeneration
  } = useBlogGenerationMutation();

  // Handle incoming prefill from Content Strategy / Opportunity
  useEffect(() => {
    const prefill = (location.state as any)?.prefill;
    if (prefill) {
      const kw = prefill.target_keyword || prefill.topic;
      if (kw) {
        setTopics([kw]);
        setDialogOpen(true);
      }
      // Clear location state so reload / site change does not re-open the dialog
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, setTopics, setDialogOpen]);

  return (
    <>
      <Card>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Blog Generation</p>

          <div className="flex flex-col items-center justify-center gap-6 p-6 pb-0 w-full mt-2.5 mb-2.5">
            <h3 className="font-medium text-lg text-center text-[#1D2939]">
              No Blog Generation Data Found
            </h3>
            <p className="font-normal text-sm leading-5 text-center text-[#667085]">
              It looks like Blog Generation hasn't been run or completed for
              this site yet. Navigate to your SEO dashboard to start generating
              blog content.
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="contained"
              disabled={siteSoftDeleted}
              sx={{
                backgroundColor: 'var(--color-primary, #120242)',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 400,
                borderRadius: '4px',
                fontSize: '14px',
                py: 1,
                px: 3.5,
                '&:hover': {
                  backgroundColor: 'rgba(18, 2, 66, 0.9)'
                }
              }}
            >
              Run Blog Generation AI
            </Button>
          </div>
        </div>
      </Card>

      <GenerateBlogPostDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        keywords={topics}
        setKeywords={setTopics}
        onEnable={(keywords) => handleEnableBlogGeneration(keywords, { siteId, userId })}
      />
    </>
  );
}

export default BGNoDataFound;
